import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { createAccessRequest, listCustomersForAccess } from "./api";

const pick = (row, keys, fallback = "") => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") {
      return row[key];
    }
  }
  return fallback;
};

const normalizeCustomerList = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.customers)) return res.customers;
  return [];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const AddRequestPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sendingId, setSendingId] = useState(null);

  const searchKey = useMemo(() => searchQuery.trim(), [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await listCustomersForAccess({
        page: currentPage,
        limit: recordsPerPage,
        search: searchKey || undefined,
      });

      const parsed = normalizeCustomerList(res);
      if (res?.status === 200) {
        setCustomers(parsed);
        const apiTotalRecords =
          Number(res?.pagination?.total_records) ||
          Number(res?.pagination?.totalRecords) ||
          Number(res?.pagination?.total) ||
          parsed.length;

        const apiTotalPages =
          Number(res?.pagination?.total_pages) ||
          Number(res?.pagination?.last_page) ||
          Math.max(1, Math.ceil(apiTotalRecords / recordsPerPage));

        setTotalRecords(apiTotalRecords);
        const safeTotalPages = Math.max(1, apiTotalPages);
        setTotalPages(safeTotalPages);
        if (currentPage > safeTotalPages) {
          setCurrentPage(safeTotalPages);
        }
      } else {
        setCustomers([]);
        setTotalRecords(0);
        setTotalPages(1);
        toast.error(res?.message || "Failed to fetch customers");
      }
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchKey, currentPage, recordsPerPage]);

  const handleSendRequest = async (customer) => {
    const customerId = pick(customer, ["id", "customer_id"], "");
    const customerName = pick(customer, ["name", "customer_name"], "-");
    const resolvedUserId = toNumber(
      pick(customer, ["user_id", "userId", "id", "customer_id", "customerId"], "")
    );

    if (!resolvedUserId) {
      toast.error("Invalid customer id");
      return;
    }

    const payload = {
      user_id: resolvedUserId,
      target_user_id: resolvedUserId,
      reason: `Access request for ${customerName}`,
    };

    setSendingId(customerId);
    const res = await createAccessRequest(payload);

    if (res?.status === 200 || res?.status === 201) {
      setSendingId(null);
      toast.success(res?.message || "Request sent successfully");
      navigate("/user-access/requests");
      return;
    }

    setSendingId(null);
    toast.error(res?.message || "Failed to send request");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mx-3 mt-4">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Add User Access Request</h2>
            </div>
            <button
              type="button"
              onClick={() => navigate("/user-access/requests")}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:-translate-y-[1px] hover:bg-slate-50"
            >
              Back to Request List
            </button>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by customer name or phone number"
              className="h-9 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-3 py-6 text-center text-sm text-slate-500">
                      Loading customers...
                    </td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-3 py-6 text-center text-sm text-slate-500">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => {
                    const customerId = pick(customer, ["id", "customer_id"], "-");
                    const customerName = pick(customer, ["name", "customer_name"], "-");
                    const customerPhone = pick(customer, ["mobile_no", "phone", "contact_number"], "-");
                    const customerEmail = pick(customer, ["email", "email_id"], "-");
                    const isSending = String(sendingId) === String(customerId);

                    return (
                      <tr key={customerId} className="border-t border-slate-100">
                        <td className="px-3 py-2.5 text-xs text-slate-700">{customerId}</td>
                        <td className="px-3 py-2.5 text-xs font-medium text-slate-900">{customerName}</td>
                        <td className="px-3 py-2.5 text-xs text-slate-700">{customerPhone}</td>
                        <td className="px-3 py-2.5 text-xs text-slate-700">{customerEmail}</td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => handleSendRequest(customer)}
                            disabled={isSending}
                            className="rounded-md bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:-translate-y-[1px] hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {isSending ? "Sending..." : "Send Request"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-2.5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-medium">Rows</span>
              <select
                value={recordsPerPage}
                onChange={(e) => {
                  setRecordsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-blue-500 focus:outline-none"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="rounded bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-700">
                {customers.length}/{totalRecords}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Prev
              </button>
              <span className="rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white">
                {currentPage} / {Math.max(1, totalPages)}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(Math.max(1, totalPages), prev + 1))}
                disabled={currentPage >= totalPages}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddRequestPage;
